import { flexRender, Table } from "@tanstack/react-table";
import { useIsMobile } from "./useIsMobile";

interface GenericTableProps<T> {
    table: Table<T>;
    title: string;
    className?: string;
    containerClassName?: string;
    theadZIndex?: string;
    rowClassName?: string;
}

const GenericTable = <T,>({
    table,
    title,
    className = "max-w-full p-0 holder w-full md:bg-card text-white pt-4 md:rounded-3xl overflow-hidden md:border mb-8 text-sm",
    containerClassName = "deploy w-full md:max-h-[70vh] md:max-w-fit overflow-y-auto",
    theadZIndex = "z-20",
    rowClassName = "relative md:border-b md:border-none md:hover:bg-card-foreground md:border-card-foreground flex w-full max-w-full flex-col md:table-row cursor-pointer md:h-18 h-fit bg-card border-b border-card-foreground rounded-4xl md:bg-transparent md:rounded-none p-4 ",
}: GenericTableProps<T>) => {
    const isMobile = useIsMobile(); // Example usage
    // You can now use isMobile to conditionally render cell content, e.g.:
    // {isMobile ? <MobileCell ... /> : flexRender(cell.column.columnDef.cell, cell.getContext())}

    return (
        <div className={className}>
            <h2 className="subtitle text-xl pl-4 hidden">{title}</h2>
            <div className={containerClassName}>
                <table className="w-full min-w-full max-w-full">
                    <thead className={`sticky top-0 ${theadZIndex} hidden md:table-header-group min-w-full max-w-full`}>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id} className="bg-card backdrop-blur-md text-white w-full max-w-full">
                                {headerGroup.headers.map((header) => (
                                    <th key={header.id} className="px-4 py-2 text-left text-sm font-thin">
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody className="flex flex-col gap-4 md:table-row-group">
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id} className={rowClassName}>
                                {row.getVisibleCells().map((cell, cellIdx, arr) => (
                                    <td key={cell.id} className="md:px-4 px-0 py-2">
                                        {isMobile ? (
                                            cellIdx === arr.length - 1 ? (
                                                <div className="absolute top-4 right-4 flex  items-center justify-between">
                                                    <span>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="flex flex-row w-full items-center justify-between">
                                                    {cellIdx !== 0 && (
                                                        <span className=" text-gray-400 mb-1">
                                                            {typeof cell.column.columnDef.header === "string"
                                                                ? cell.column.columnDef.header
                                                                : typeof cell.column.columnDef.header === "function"
                                                                  ? cell.column.id
                                                                  : cell.column.columnDef.header}
                                                            {": "}
                                                        </span>
                                                    )}
                                                    <span>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </span>
                                                </div>
                                            )
                                        ) : (
                                            <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default GenericTable;
